// File: src/lib/firebase.ts
//
// Firebase bootstrap — SAFE + resilient
// - Env resolution supports both VITE_FB_* and VITE_FIREBASE_*
// - Auth persistence fallback chain (no hard failures if blocked)
// - Firestore/Storage/Functions init
// - RTDB init ONLY if databaseURL configured; exports rtdb: Database | null
// - Emulator wiring is guarded + idempotent
// - Debug loglevel support
// - Anonymous auth helper remains opt-in (call ensureAnon only when desired)
// - Media registry resolver install (provider="registry") is global + idempotent
//
// V1 improvement:
// - If USE_EMULATORS is true, Auth emulator is ON by default (unless explicitly disabled).
// - Exposes `useEmulators` for compatibility with existing imports.
//
// IMPORTANT FIXES IN THIS REWRITE:
// - USE_EMULATORS is defined BEFORE cfg so storageBucket can be computed correctly.
// - storageBucket switches to *.appspot.com under emulators (required for Storage emulator auth/rules behavior).
// - Firebase logging setLogLevel is applied safely.
// - ProjectId debug + __synk_projectId assignment is safe + early (after app init).
// - Default export is `app` (null when disabled).
// - DATABASE_URL treated as optional if missing/placeholder.

import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInAnonymously,
  onAuthStateChanged,
  connectAuthEmulator,
  type User,
  type Auth,
  inMemoryPersistence,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  setLogLevel,
  type Firestore,
} from "firebase/firestore";
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from "firebase/functions";
import {
  getDatabase,
  connectDatabaseEmulator,
  type Database,
} from "firebase/database";

import { installFirestoreMediaRegistryResolver } from "@/services/media/registry/firestoreRegistryResolver";

/* --------------------------------------------------------------------
   Env helpers (supports both VITE_FB_* and VITE_FIREBASE_*)
-------------------------------------------------------------------- */
function env(...keys: string[]) {
  const e = (import.meta as any)?.env ?? {};
  for (const k of keys) {
    const v = e[k];
    if (typeof v === "string" && v.length) return v;
  }
  return undefined;
}

function envBool(...keys: string[]) {
  const v = env(...keys);
  if (!v) return false;
  const s = v.toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function isPlaceholder(v: unknown) {
  const s = String(v ?? "").trim();
  if (!s) return true;
  return s.startsWith("REPLACE_ME") || s.includes("REPLACE_ME");
}

/* --------------------------------------------------------------------
   Emulator mode (MUST be above cfg if cfg depends on it)
-------------------------------------------------------------------- */
const USE_EMULATORS =
  envBool("VITE_USE_EMULATORS", "VITE_USE_FIREBASE_EMULATORS") ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"));

// ✅ Auth emulator defaults ON when emulators are in use
const USE_AUTH_EMULATOR = envBool("VITE_USE_AUTH_EMULATOR") || USE_EMULATORS;

// Optional hard-disable for auth emulator if you want it
const DISABLE_AUTH_EMULATOR = envBool("VITE_DISABLE_AUTH_EMULATOR");

/* Emulator host defaults */
const EMU_HOST = env("VITE_FIREBASE_EMULATOR_HOST") || "127.0.0.1";
const EMU_AUTH_PORT = Number(env("VITE_FIREBASE_AUTH_PORT") || 9099);
const EMU_FS_PORT = Number(env("VITE_FIREBASE_FS_PORT") || 8080);
const EMU_STORAGE_PORT = Number(env("VITE_FIREBASE_STORAGE_PORT") || 9199);
const EMU_FUNCTIONS_PORT = Number(env("VITE_FIREBASE_FUNCTIONS_PORT") || 5001);
const EMU_RTDB_PORT = Number(env("VITE_FIREBASE_RTDB_PORT") || 9000);

/* --------------------------------------------------------------------
   Firebase config (prefer env; fall back to placeholders to avoid build breaks)
   - storageBucket is computed so emulators use the .appspot.com bucket name
-------------------------------------------------------------------- */
const PROJECT_ID =
  env("VITE_FIREBASE_PROJECT_ID", "VITE_FB_PROJECT_ID") ||
  "synktribe-auralis-8135d";

// Raw bucket (what you use in prod config)
const rawBucket =
  env("VITE_FIREBASE_STORAGE_BUCKET", "VITE_FB_STORAGE_BUCKET") ||
  "synktribe-auralis-8135d.firebasestorage.app";

// ✅ Under emulators, Storage should use the classic bucket name
const storageBucket = USE_EMULATORS ? `${PROJECT_ID}.appspot.com` : rawBucket;

const cfg = {
  apiKey:
    env("VITE_FIREBASE_API_KEY", "VITE_FB_API_KEY") ||
    "AIzaSyARs-7eRIfCbkqYqbvqKodkFRvzEa5zYyk",
  authDomain:
    env("VITE_FIREBASE_AUTH_DOMAIN", "VITE_FB_AUTH_DOMAIN") ||
    `${PROJECT_ID}.firebaseapp.com`,
  projectId: PROJECT_ID,
  storageBucket,
  messagingSenderId:
    env("VITE_FIREBASE_MESSAGING_SENDER_ID", "VITE_FB_MESSAGING_SENDER_ID") ||
    "527605464858",
  appId:
    env("VITE_FIREBASE_APP_ID", "VITE_FB_APP_ID") ||
    "1:527605464858:web:022dc0295897155346b6a3",
} as const;

// Optional RTDB URL (only initialize RTDB if present AND not placeholder)
const DATABASE_URL =
  env("VITE_FIREBASE_DATABASE_URL", "VITE_FB_DATABASE_URL") ||
  "https://synktribe-auralis-8135d-default-rtdb.europe-west1.firebasedatabase.app";

/** Firebase Functions region */
const FUNCTIONS_REGION =
  env("VITE_FB_FUNCTIONS_REGION", "VITE_FIREBASE_FUNCTIONS_REGION") ||
  "us-central1";

const FIREBASE_DEBUG = envBool("VITE_FIREBASE_DEBUG");

/* --------------------------------------------------------------------
   Safety checks
-------------------------------------------------------------------- */
function assertConfigPresent(o: Record<string, any>) {
  const missing = Object.entries(o)
    .filter(([k, v]) => {
      if (k === "databaseURL") return false; // optional
      return (
        !v ||
        String(v).trim().length === 0 ||
        String(v).startsWith("REPLACE_ME")
      );
    })
    .map(([k]) => k);

  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Firebase Config Warning] Missing/placeholder values: ${missing.join(", ")}`,
    );
  }
}

function isFirebaseConfigured() {
  return (
    !isPlaceholder(cfg.apiKey) &&
    !isPlaceholder(cfg.authDomain) &&
    !isPlaceholder(cfg.projectId) &&
    !isPlaceholder(cfg.storageBucket) &&
    !isPlaceholder(cfg.messagingSenderId) &&
    !isPlaceholder(cfg.appId)
  );
}

/* --------------------------------------------------------------------
   Initialize App (idempotent)
-------------------------------------------------------------------- */
const ENABLED = isFirebaseConfigured();

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let functions: Functions | null = null;
let rtdb: Database | null = null;

if (ENABLED) {
  app = getApps().length ? getApp() : initializeApp(cfg);
  assertConfigPresent(cfg);

  // Project debug (safe + useful for scripts and emu parity)
  try {
    // eslint-disable-next-line no-console
    console.log("[firebase] projectId =", app.options.projectId);
    (globalThis as any).__synk_projectId = app.options.projectId;
  } catch {
    // ignore
  }
} else {
  // eslint-disable-next-line no-console
  console.warn(
    "[firebase] Firebase disabled: missing/placeholder config (no init).",
  );
}

/* --------------------------------------------------------------------
   Auth init (persistence fallback chain)
-------------------------------------------------------------------- */
function initAuth(a: FirebaseApp): Auth {
  // Idempotent acquisition
  let instance: Auth;
  try {
    instance = getAuth(a);
  } catch {
    instance = initializeAuth(a, {
      persistence: USE_EMULATORS
        ? [inMemoryPersistence]
        : [
            indexedDBLocalPersistence,
            browserLocalPersistence,
            browserSessionPersistence,
          ],
    });
  }

  // Persistence strategy
  if (USE_EMULATORS) {
    // Under emulators, avoid pulling any persisted prod tokens
    setPersistence(instance, inMemoryPersistence).catch(() => void 0);

    // Best-effort: clear any preloaded user
    if (instance.currentUser) {
      signOut(instance).catch(() => void 0);
    }
  } else {
    // Normal persistence chain (best effort)
    setPersistence(instance, browserLocalPersistence).catch(() =>
      setPersistence(instance, indexedDBLocalPersistence).catch(() =>
        setPersistence(instance, browserSessionPersistence).catch(() => void 0),
      ),
    );
  }

  return instance;
}

if (app) {
  auth = initAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app, FUNCTIONS_REGION);

  // RTDB (optional)
  const canInitRtdb = DATABASE_URL && !isPlaceholder(DATABASE_URL);
  if (canInitRtdb) {
    try {
      rtdb = getDatabase(app, DATABASE_URL);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[firebase] Failed to initialize RTDB; continuing without RTDB.",
        err,
      );
      rtdb = null;
    }
  }
}

/* --------------------------------------------------------------------
   Emulator wiring (browser only; idempotent)
-------------------------------------------------------------------- */
function markOnce(key: string): boolean {
  const g = globalThis as any;
  g.__synk_emulators = g.__synk_emulators || {};
  if (g.__synk_emulators[key]) return true;
  g.__synk_emulators[key] = true;
  return false;
}

if (typeof window !== "undefined" && USE_EMULATORS && app) {
  // Auth emulator
  if (auth && USE_AUTH_EMULATOR && !DISABLE_AUTH_EMULATOR) {
    if (!markOnce("auth")) {
      try {
        connectAuthEmulator(auth, `http://${EMU_HOST}:${EMU_AUTH_PORT}`, {
          disableWarnings: true,
        });
      } catch {
        // ignore
      }
    }
  }

  // Firestore emulator
  if (db) {
    if (!markOnce("firestore")) {
      try {
        connectFirestoreEmulator(db, EMU_HOST, EMU_FS_PORT);
      } catch {
        // ignore
      }
    }
  }

  // Storage emulator
  if (storage) {
    if (!markOnce("storage")) {
      try {
        connectStorageEmulator(storage, EMU_HOST, EMU_STORAGE_PORT);
      } catch {
        // ignore
      }
    }
  }

  // Functions emulator
  if (functions) {
    if (!markOnce("functions")) {
      try {
        connectFunctionsEmulator(functions, EMU_HOST, EMU_FUNCTIONS_PORT);
      } catch {
        // ignore
      }
    }
  }

  // RTDB emulator
  if (rtdb) {
    if (!markOnce("rtdb")) {
      try {
        connectDatabaseEmulator(rtdb, EMU_HOST, EMU_RTDB_PORT);
      } catch {
        // ignore
      }
    }
  }

  // eslint-disable-next-line no-console
  console.info(
    `[Firebase Emulator] host=${EMU_HOST} auth=${
      auth && USE_AUTH_EMULATOR && !DISABLE_AUTH_EMULATOR ? EMU_AUTH_PORT : "off"
    } fs=${db ? EMU_FS_PORT : "off"} storage=${
      storage ? EMU_STORAGE_PORT : "off"
    } fn=${functions ? EMU_FUNCTIONS_PORT : "off"} rtdb=${
      rtdb ? EMU_RTDB_PORT : "off"
    } bucket=${cfg.storageBucket}`,
  );
}

/* --------------------------------------------------------------------
   Logging
-------------------------------------------------------------------- */
if (FIREBASE_DEBUG && ENABLED) {
  try {
    setLogLevel("debug");
  } catch {
    // ignore
  }
}

/* --------------------------------------------------------------------
   Media registry resolver install (idempotent)
-------------------------------------------------------------------- */
function installMediaRegistryResolverOnce() {
  if (!db) return;

  const g = globalThis as any;
  g.__oc_media_registry = g.__oc_media_registry || {};
  if (g.__oc_media_registry._firestoreInstalled) return;

  try {
    const uninstall = installFirestoreMediaRegistryResolver({ firestore: db });
    g.__oc_media_registry._firestoreInstalled = true;
    g.__oc_media_registry._firestoreUninstall = uninstall;

    // eslint-disable-next-line no-console
    console.info("[mediaRegistry] Firestore registry resolver installed");
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      "[mediaRegistry] Failed to install Firestore registry resolver",
      err,
    );
  }
}

if (typeof window !== "undefined" && ENABLED) {
  installMediaRegistryResolverOnce();
}

/* --------------------------------------------------------------------
   DEV: media registry helpers and opt-in seeding only
-------------------------------------------------------------------- */
const DEV_AUTO_SEED_MEDIA_REGISTRY = envBool(
  "VITE_DEV_AUTO_SEED_MEDIA_REGISTRY",
  "VITE_AUTO_SEED_MEDIA_REGISTRY",
);

function shouldAutoSeedMediaRegistryDev(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined" || !USE_EMULATORS) {
    return false;
  }

  if (DEV_AUTO_SEED_MEDIA_REGISTRY) return true;

  try {
    const url = new URL(window.location.href);
    const queryValue = url.searchParams.get("seedMediaRegistry");
    if (queryValue === "1" || queryValue === "true") return true;

    const persistedValue =
      window.localStorage?.getItem("oc:seedMediaRegistry") ||
      window.sessionStorage?.getItem("oc:seedMediaRegistry");
    return persistedValue === "1" || persistedValue === "true";
  } catch {
    return false;
  }
}

let mediaRegistrySeedPromise: Promise<void> | null = null;

async function runMediaRegistrySeedDev(): Promise<void> {
  if (mediaRegistrySeedPromise) return mediaRegistrySeedPromise;

  mediaRegistrySeedPromise = (async () => {
    if (FIREBASE_USE_AUTH_EMULATOR && auth && !auth.currentUser) {
      try {
        await ensureAnon();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[seedMediaRegistry] anon auth setup failed", err);
      }
    }

    const mod = await import("@/dev/seedMediaRegistry");
    await mod.seedMediaRegistry_DEV();
  })();

  try {
    await mediaRegistrySeedPromise;
  } finally {
    mediaRegistrySeedPromise = null;
  }
}

function installMediaRegistryDevWindowHooksOnce() {
  if (typeof window === "undefined" || !import.meta.env.DEV) return;

  const w = window as any;
  w.__oc_media = w.__oc_media || {};

  if (typeof w.__oc_debug_registry !== "function") {
    w.__oc_debug_registry = async (key: string) => {
      if (!w.__oc_media?.resolveRegistryToUrl) {
        throw new Error("window.__oc_media.resolveRegistryToUrl not installed");
      }

      const url = await w.__oc_media.resolveRegistryToUrl({
        v: 1,
        provider: "registry",
        key,
        kind: "audio",
      });

      return {
        key,
        url: url ?? null,
        meta: w.__oc_media.__lastRegistryMeta ?? null,
        last: w.__oc_media.__lastRegistryResolve ?? null,
      };
    };
  }

  if (typeof w.__oc_clear_media_registry_seed_flag !== "function") {
    w.__oc_clear_media_registry_seed_flag = () => {
      const g = globalThis as any;
      g.__synk_dev_seeds = g.__synk_dev_seeds || {};
      delete g.__synk_dev_seeds.mediaRegistry_v1;
      // eslint-disable-next-line no-console
      console.info("[seedMediaRegistry] cleared once flag (mediaRegistry_v1)");
    };
  }

  if (typeof w.__oc_seed_media_registry !== "function") {
    w.__oc_seed_media_registry = async () => {
      await runMediaRegistrySeedDev();
      return true;
    };
  }
}

function scheduleMediaRegistryDevSeed() {
  if (!shouldAutoSeedMediaRegistryDev() || typeof window === "undefined") return;

  const run = () => {
    void runMediaRegistrySeedDev().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[seedMediaRegistry] auto-seed failed", err);
    });
  };

  if ("requestIdleCallback" in window) {
    (window as Window & {
      requestIdleCallback: (callback: () => void, options?: { timeout: number }) => number;
    }).requestIdleCallback(run, { timeout: 1500 });
    return;
  }

  globalThis.setTimeout(run, 600);
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  installMediaRegistryDevWindowHooksOnce();
  scheduleMediaRegistryDevSeed();
}

/* --------------------------------------------------------------------
   Anonymous Auth helper (opt-in, idempotent)
-------------------------------------------------------------------- */
let ensureAnonPromise: Promise<User> | null = null;

export async function ensureAnon(): Promise<User> {
  if (!auth) {
    throw new Error("[firebase] ensureAnon called but Firebase auth is disabled.");
  }
  if (auth.currentUser) return auth.currentUser;
  if (ensureAnonPromise) return ensureAnonPromise;

  ensureAnonPromise = (async () => {
    // Allow an auth state to land without forcing sign-in
    const existing: User | null = await new Promise((resolve) => {
      let settled = false;
      const unsub = onAuthStateChanged(auth as Auth, (u) => {
        if (!settled) {
          settled = true;
          try {
            unsub();
          } catch {
            // ignore
          }
          resolve(u);
        }
      });

      queueMicrotask(() => {
        if (!settled) {
          settled = true;
          try {
            unsub();
          } catch {
            // ignore
          }
          resolve(null);
        }
      });
    });

    if (existing) return existing;

    const { user } = await signInAnonymously(auth as Auth);
    return user;
  })();

  try {
    return await ensureAnonPromise;
  } finally {
    ensureAnonPromise = null;
  }
}

/* --------------------------------------------------------------------
   Exports (plus compatibility aliases)
-------------------------------------------------------------------- */
export { app, auth, db, storage, functions, rtdb };

export const FIREBASE_ENABLED = ENABLED;
export const IS_USING_EMULATORS = USE_EMULATORS;

// ✅ Compatibility for existing imports in app code
export const useEmulators = USE_EMULATORS;

export const FIREBASE_DATABASE_URL = DATABASE_URL;
export const FIREBASE_FUNCTIONS_REGION = FUNCTIONS_REGION;
export const FIREBASE_USE_AUTH_EMULATOR =
  USE_AUTH_EMULATOR && !DISABLE_AUTH_EMULATOR;

export async function initFirebase() {
  return app;
}

export function getAuthOrThrow(): Auth {
  if (!auth) {
    throw new Error("[firebase] Auth is not initialized (Firebase disabled).");
  }
  return auth;
}

export function getDbOrThrow(): Firestore {
  if (!db) {
    throw new Error("[firebase] Firestore is not initialized (Firebase disabled).");
  }
  return db;
}

export function getStorageOrThrow(): FirebaseStorage {
  if (!storage) {
    throw new Error("[firebase] Storage is not initialized (Firebase disabled).");
  }
  return storage;
}

export function getFunctionsOrThrow(): Functions {
  if (!functions) {
    throw new Error("[firebase] Functions is not initialized (Firebase disabled).");
  }
  return functions;
}

export default app;
